/* eslint-disable no-param-reassign */
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Link} from 'react-router-dom';
import {toast} from 'react-toastify';
import Swal from 'sweetalert2';
import Tour from 'reactour';
import ReactTooltip from 'react-tooltip';
import {useAuth} from '../../hooks/Auth';
import ActionButton from '../../components/ActionButton';
import Button from '../../components/Button';
import CardBody from '../../components/CardBody';
import FullPageLoader from '../../components/FullPageLoader';
import Table from '../../components/Table';
import TitlePage from '../../components/TitlePage';
import api from '../../services/api';
import ModalForm from './Modal';
import columns from './schema';
import {ServiceCompositionProps, TableOrder} from '../../interfaces';
import {tourStepsServices} from '../../data/tour';
import SaveOrderButton from '../../components/SaveOrderButton';
import importLegacyJSON from '../../services/importLegacyJSON';

const ServiceComposition: React.FC = () => {
    const {user, updateUser, tablesOrder} = useAuth();
    const tableOrder = tablesOrder.find(
        (item) => item.table === 'serviceComposition'
    );
    const [isTourOpen, setIsTourOpen] = useState(user.tour_tables || false);
    const [overflow, setOverflow] = useState(true);
    const disabledOverflow = () => {
        document.body.style.overflow = overflow ? 'hidden' : 'unset';
    };
    const [order, setOrder] = useState<TableOrder>({
        order: tableOrder?.field || 'created_at',
        type: tableOrder?.type || 'desc'
    });
    const [loading, setLoading] = useState(false);
    const [services, setServices] = useState<ServiceCompositionProps[]>([]);
    const [count, setCount] = useState(0);
    const [perPage, setPerPage] = useState(0);
    const [refresh, setRefresh] = useState(0);
    const [currentPage, setCurrentPage] = useState(0);
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const [modalVisible, setModalVisible] = useState(false);
    const [modalData, setModalData] = useState<ServiceCompositionProps>(
        {} as ServiceCompositionProps
    );

    function resetData() {
        setModalData({
            description: '',
            form_action: 'include',
            action: 0,
            icms: false,
            multiply_charge: false,
            calc: 0,
            cofins: 0,
            percentage_inc: 0,
            pis: 0
        });
    }

    async function showService(id: string, form_action: 'view' | 'edit') {
        setLoading(true);
        try {
            const response = await api.get(`services/${id}`);
            setModalData({
                ...response.data,
                form_action
            });
            setModalVisible(true);
        } catch (err) {
            toast.error('Não foi possível efetuar a consulta');
        }
        setLoading(false);
    }

    async function getServices() {
        try {
            setLoading(true);
            const response = await api.get(
                `services?page=${page}&limit=20&order=${order.order}&type=${order.type}&search=${search}`
            );
            setServices(response.data.data);
            setCount(response.data.meta.total);
            setPerPage(response.data.meta.per_page);
            setCurrentPage(response.data.meta.current_page);
            setLoading(false);
        } catch (err) {
            setServices([]);
            toast.error('Não foi possível efetuar a consulta');
            setLoading(false);
        }
    }

    useEffect(() => {
        getServices();
    }, [page, refresh, order]); // GET SERVICE COMPOSITIONS

    function handleDelete(id: string, description: string) {
        Swal.fire({
            title: 'Confirmar exclusão do registro?',
            icon: 'question',
            showCancelButton: true,
            showCloseButton: true,
            reverseButtons: true,
            confirmButtonText: 'Sim',
            cancelButtonText: 'Não'
        }).then(async (result) => {
            if (result.value) {
                try {
                    setLoading(true);
                    await api.delete(
                        `services/${id}?description=${description}`
                    );
                    setRefresh(Math.random);
                    toast.success('Registro excluído com sucesso');
                } catch (error: any) {
                    toast.error(
                        error.response?.data ||
                            'Ocorreu um erro ao excluir o registro'
                    );
                    setLoading(false);
                }
            }
        });
    }

    const mapTable = useMemo(
        () =>
            services.map((data) => (
                <tr key={data.id}>
                    <td className="pt-2">{data.description}</td>
                    <td className="pl-4">
                        <ActionButton
                            view={async () => {
                                await showService(data.id || '', 'view');
                            }}
                            edit={async () => {
                                await showService(data.id || '', 'edit');
                            }}
                            delete={() => {
                                handleDelete(data.id || '', data.description);
                            }}
                        />
                    </td>
                </tr>
            )),
        [services]
    );

    return (
        <>
            <div className="content-header desktop">
                <div className="title-page">
                    <div className="container-fluid pl-1 pr-2 d-flex">
                        <TitlePage
                            title="Composição de Serviços"
                            icon="fa-box-open"
                            tabRight
                            pagination
                            count={count}
                            currentPage={currentPage}
                            perPage={perPage}
                            customCol="4"
                        />
                        <div className="tab-min-top col-3 text-right mt-1">
                            <SaveOrderButton
                                id={tableOrder?.id as string}
                                table="serviceComposition"
                                field={order.order}
                                type={order.type}
                                setOrder={setOrder}
                            />

                            <Link to="#">
                                <Button
                                    onClick={() => {
                                        resetData();
                                        setModalVisible(true);
                                    }}
                                    className="tour-services--01"
                                    type="button"
                                    color="success"
                                    caption="Incluir"
                                    icon="fa-plus"
                                    maxSize
                                />
                            </Link>
                            <label
                                id="btn-import"
                                htmlFor="json_input"
                                className="cursor-pointer btn btn-default"
                                data-tip="Importar cadastros via JSON"
                                data-for="services-main-page-tooltip"
                                style={{marginLeft: 6}}
                            >
                                <i className="fas fa-download" />
                            </label>

                            <input
                                id="json_input"
                                type="file"
                                accept=".json"
                                style={{display: 'none'}}
                                onChange={(event) => {
                                    const reader = new FileReader();
                                    reader.onload = async function (e) {
                                        const json = e.target?.result as string;
                                        await importLegacyJSON(
                                            json,
                                            'legacyServices',
                                            setLoading,
                                            () => setRefresh(Math.random())
                                        );
                                    };
                                    reader.readAsText(
                                        event.target.files
                                            ? (event.target.files[0] as Blob)
                                            : ('' as any),
                                        'ISO-8859-1'
                                    );
                                    event.target.value = '';
                                }}
                            />
                        </div>

                        <div className="col-sm-5 col-xs-12 tab-min-top">
                            <div className="input-group tab-max-left tour-services--02">
                                <input
                                    onKeyUp={(e) => {
                                        if (e.key === 'Enter') {
                                            if (page === 1) {
                                                setRefresh(Math.random());
                                            } else {
                                                setPage(1);
                                            }
                                        }
                                    }}
                                    type="text"
                                    placeholder="Buscar por descrição"
                                    className="form-control"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                />
                                <span className="input-group-append">
                                    <Button
                                        type="button"
                                        color="default"
                                        onClick={() => {
                                            if (page === 1) {
                                                setRefresh(Math.random());
                                            } else {
                                                setPage(1);
                                            }
                                        }}
                                        icon="fa-search"
                                        notTab={true}
                                    />
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="content-header mobile">
                <div className="title-page">
                    <div className="container-fluid pl-1 pr-2">
                        <TitlePage
                            title="Composição de Serviços"
                            icon="fa-box-open"
                            tabRight={true}
                            pagination={true}
                            count={count}
                            currentPage={currentPage}
                            perPage={perPage}
                            mobile={true}
                        />
                        <div className="d-flex">
                            <div className="col-sm-3 my-2 pt-min">
                                <SaveOrderButton
                                    id={tableOrder?.id as string}
                                    table="serviceComposition"
                                    field={order.order}
                                    type={order.type}
                                    setOrder={setOrder}
                                />

                                <Link to="#">
                                    <Button
                                        onClick={() => {
                                            resetData();
                                            setModalVisible(true);
                                        }}
                                        type="button"
                                        color="success"
                                        caption="Incluir"
                                        icon="fa-plus"
                                        maxSize={true}
                                    />
                                </Link>
                                <label
                                    id="btn-import"
                                    htmlFor="json_input"
                                    className="cursor-pointer btn btn-default"
                                    data-tip="Importar cadastros via JSON"
                                    data-for="services-main-page-tooltip"
                                    style={{marginLeft: 6}}
                                >
                                    <i className="fas fa-download" />
                                </label>

                                <input
                                    id="json_input"
                                    type="file"
                                    accept=".json"
                                    style={{display: 'none'}}
                                    onChange={(event) => {
                                        const reader = new FileReader();
                                        reader.onload = async function (e) {
                                            const json = e.target
                                                ?.result as string;
                                            await importLegacyJSON(
                                                json,
                                                'legacyServices',
                                                setLoading,
                                                () => setRefresh(Math.random())
                                            );
                                        };
                                        reader.readAsText(
                                            event.target.files
                                                ? (event.target
                                                      .files[0] as Blob)
                                                : ('' as any),
                                            'ISO-8859-1'
                                        );
                                        event.target.value = '';
                                    }}
                                />
                            </div>
                            <div className="col-sm-9 col-xs-12 my-2">
                                <div className="input-group tab-max-left">
                                    <input
                                        onKeyUp={(e) => {
                                            if (e.key === 'Enter') {
                                                if (page === 1) {
                                                    setRefresh(Math.random());
                                                } else {
                                                    setPage(1);
                                                }
                                            }
                                        }}
                                        type="text"
                                        placeholder="Buscar por descrição"
                                        className="form-control"
                                        value={search}
                                        onChange={(e) =>
                                            setSearch(e.target.value)
                                        }
                                    />
                                    <span className="input-group-append">
                                        <Button
                                            type="button"
                                            color="default"
                                            onClick={() => {
                                                if (page === 1) {
                                                    setRefresh(Math.random());
                                                } else {
                                                    setPage(1);
                                                }
                                            }}
                                            icon="fa-search"
                                            notTab={true}
                                        />
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <section>
                <div className="container-fluid">
                    <div className="row">
                        <CardBody
                            size="12"
                            smallSize="12"
                            page={page}
                            count={count}
                            setPage={setPage}
                        >
                            <Table
                                id="groups"
                                columns={columns}
                                setOrder={setOrder}
                                order={order.order}
                                type={order.type}
                            >
                                {mapTable}
                            </Table>
                        </CardBody>
                    </div>
                </div>
            </section>

            {modalVisible && (
                <ModalForm
                    data={modalData}
                    refresh={() => setRefresh(Math.random())}
                    hide={() => setModalVisible(false)}
                />
            )}

            {loading ? (
                <FullPageLoader />
            ) : (
                <Tour
                    steps={tourStepsServices}
                    isOpen={isTourOpen}
                    onRequestClose={() => setIsTourOpen(false)}
                    accentColor="#007BFF"
                    maskSpace={8}
                    rounded={20}
                    startAt={0}
                    onAfterOpen={() => {
                        setOverflow(false);
                        disabledOverflow();
                    }}
                    onBeforeClose={() => {
                        if (user.tour_tables) {
                            api.post('tourTables');
                            updateUser({
                                ...user,
                                tour_tables: false
                            });
                        }
                        setOverflow(true);
                        disabledOverflow();
                    }}
                />
            )}

            <ReactTooltip
                id="services-main-page-tooltip"
                place="left"
                effect="solid"
                backgroundColor="#42494E"
                globalEventOff="click"
                delayShow={150}
            />
        </>
    );
};

export default ServiceComposition;
